import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save } from "lucide-react";
import { ModuleDetailsForm, SyllabusSection, MaterialsSection, VideoLinksSection, GallerySection } from "@/components/ModuleEditorSections";

export default function AdminModuleEdit() {
  const { id } = useParams();
  const [mod, setMod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/modules/${id}`).then((r) => setMod(r.data)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveModule = useCallback(async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/modules/${id}`, updates);
      setMod(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }, [id]);

  const handleFieldSave = () => {
    saveModule({
      title: mod.title,
      description: mod.description,
      what_youll_learn: mod.what_youll_learn,
      notes: mod.notes,
    });
  };

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center py-20"><div className="text-[#5C3A2E]/50">Loading...</div></div></AdminLayout>;
  }

  if (!mod) {
    return <AdminLayout><div className="text-center py-20"><p className="text-[#2B2B2B]/50">Module not found</p></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div data-testid="admin-module-edit" className="space-y-8 animate-fade-in-up max-w-3xl">
        <div className="flex items-center justify-between">
          <Link to="/admin/modules" className="inline-flex items-center gap-2 text-sm text-[#2B2B2B]/50 hover:text-[#5C3A2E] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Modules
          </Link>
          <Button data-testid="save-all-button" onClick={handleFieldSave} disabled={saving} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        <ModuleDetailsForm mod={mod} setMod={setMod} />
        <SyllabusSection mod={mod} onSave={saveModule} />
        <MaterialsSection mod={mod} onSave={saveModule} />
        <VideoLinksSection mod={mod} onSave={saveModule} />
        <GallerySection mod={mod} onSave={saveModule} />
      </div>
    </AdminLayout>
  );
}
